import React, { useCallback, useEffect, useState } from 'react';
import { useObserver } from 'mobx-react-lite';
import styled from 'styled-components';
import tagsStore from '../../../stores/tags.store';
import serverStore from '../../../stores/server.store';
import mongodbStore from '../../../stores/mongodb.store';
import { Redirect, useHistory } from 'react-router-dom';
import uptimeStore from '../../../stores/uptime.store';
import LoaderComponent from '../../components/Loader.component';
import Header from '../../components/header/Header';
import { DeleteServerZoneStyled, EditZoneStyled, StyledEditWrapper, TagsWrapperStyled } from './ServerEdit.page';
import { EuiButton, EuiIcon } from '@elastic/eui';
import { GoBackStyled } from '../../../style/components/common.styled';
import EditServerName from './components/serverEdit/EditServerName';
import TagsList from '../../components/tags/TagsList';
import { MONITOR_TYPES } from '../../../constants/monitor.types';
import mongoDbStore from '../../../stores/mongodb.store';
import { addToast } from '../../components/toasts/Toast';
import TagsModal from '../../components/tags/TagsModal';
import { loadDetailData } from '../../../servises/detail-loading.service';
import MonitorLoadError from './components/MonitorLoadError';

const EditSurfaceStyled = styled.div`
  width: 100%;
  min-height: 200px;
  padding: 18px;
  border-radius: 8px;
  background-color: rgb(29,30,36);
  box-shadow: rgb(0 0 0 / 28%) 0px 0.9px 4px, rgb(0 0 0 / 21%) 0px 2.6px 8px, rgb(0 0 0 / 18%) 0px 5.7px 12px, rgb(0 0 0 / 14%) 0px 15px 15px;

  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const EditHeroStyled = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 20px;
  align-items: start;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }

  .eyebrow {
    color: #36A2EF;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 7px;
    text-transform: uppercase;
  }

  .summary {
    color: rgba(255, 255, 255, 0.62);
    font-size: 13px;
    line-height: 1.55;
    margin-top: 8px;
    max-width: 680px;
  }

  .hero-actions {
    display: flex;
    justify-content: flex-end;

    @media (max-width: 760px) {
      justify-content: flex-start;
    }
  }
`;

const MongoMetaGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  grid-gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MongoMetaItemStyled = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 6px;
  min-width: 0;
  padding: 10px 12px;

  .label {
    color: rgba(255, 255, 255, 0.48);
    display: block;
    font-size: 11px;
    margin-bottom: 5px;
  }

  .value {
    color: rgba(255, 255, 255, 0.9);
    display: block;
    font-family: Consolas, Monaco, monospace;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ManagementGridStyled = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.8fr);
  grid-gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ColumnStyled = styled.div`
  display: grid;
  grid-gap: 16px;
  align-content: start;
`;

const SectionHeaderStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;

  .section-icon {
    align-items: center;
    background: rgba(0, 128, 213, 0.12);
    border: 1px solid rgba(0, 128, 213, 0.25);
    border-radius: 6px;
    color: #36A2EF;
    display: flex;
    flex: 0 0 30px;
    height: 30px;
    justify-content: center;
    width: 30px;
  }

  p {
    color: rgba(255, 255, 255, 0.58);
    font-size: 12px;
    line-height: 1.45;
    margin: 5px 0 0;
  }
`;

const TagsActionsStyled = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const InsightListStyled = styled.div`
  display: grid;
  gap: 10px;

  .insight-row {
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.055);
    border-radius: 6px;
    display: grid;
    gap: 3px;
    padding: 10px 12px;
  }

  .label {
    color: rgba(255, 255, 255, 0.48);
    font-size: 11px;
  }

  .value {
    color: rgba(255, 255, 255, 0.9);
    font-family: Consolas, Monaco, monospace;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DangerActionsStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MongoEditPage = props => {
    document.title = 'Mongo Edit - SyAgent';
    const { serverId } = props.match.params;

    const redirect = () => {
        props.history.push('/servers');
    };

    return useObserver(() => (
        <Observed
            serverId={ serverId }
            handleRedirect={ redirect }
            servers={ mongodbStore.mongoDbListObj }
        />
    ));
};

const Observed = (props) => {
    const {
        serverId,
        servers,
        handleRedirect,
    } = props;

    const history = useHistory();
    const [loader, setLoader] = useState(true);
    const [loadedServerId, setLoadedServerId] = useState(null);
    const [mongoListLoaded, setMongoListLoaded] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadSequence, setLoadSequence] = useState(0);
    const [areYouSureState, setAreYouSureState] = useState(false);
    const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
    const [deleteSending, setDeleteSending] = useState(false);

    const server = servers[serverId];
    const metric = server && server.metric ? server.metric : null;
    const serverStatus = metric && metric.serverStatus ? metric.serverStatus : null;
    const mongoServerId = server ? server._id : null;

    useEffect(() => {
        let active = true;
        setLoader(true);
        setMongoListLoaded(false);
        setLoadFailed(false);
        const getsData = async () => {
            const secondaryFailed = await loadDetailData(
                mongoDbStore.getMongoDb(true),
                [
                    serverStore.getServers(),
                    tagsStore.getTags(),
                    uptimeStore.getUptime()
                ]
            );
            if (secondaryFailed) {
                addToast('Some monitor settings could not be loaded', 'error');
            }
            if (active) {
                setMongoListLoaded(true);
            }
        };

        getsData()
            .catch(error => {
                addToast(error.message || 'Failed to load monitor', 'error');
                if (active) {
                    setLoadFailed(true);
                }
            })
            .finally(() => {
                if (active) {
                    setLoadedServerId(serverId);
                    setLoader(false);
                }
            });
        return () => {
            active = false;
        };
    }, [serverId, loadSequence]);

    const handleDelete = useCallback(async e => {
        if (deleteSending) {
            return;
        }

        setDeleteSending(true);
        try {
            await mongodbStore.deleteServer(mongoServerId);
            addToast('Mongo monitor deleted', 'suc');
            handleRedirect();
        } catch (error) {
            addToast(error.message || 'Failed to delete Mongo monitor', 'error');
        } finally {
            setDeleteSending(false);
        }

    }, [deleteSending, handleRedirect, mongoServerId]);

    const handleNameSave = useCallback(async (id, name) => {
        await mongodbStore.updateMongo(id, name);
    }, []);

    const goSomewhere = e => {
        e.preventDefault();
        history.push(`/mongo/${ serverId }`);
    };

    return (
        <main className="servers">
            { (loader || loadedServerId !== serverId) && (
                <LoaderComponent/>
            ) }
            { !loader && loadedServerId === serverId && mongoListLoaded && !server && (
                <Redirect to="/404"/>
            ) }
            { !loader && loadedServerId === serverId && loadFailed && (
                <MonitorLoadError onRetry={ () => setLoadSequence(value => value + 1) }/>
            ) }
            { !loader && loadedServerId === serverId && !loadFailed && mongoListLoaded && server && (
                <>
                    <Header/>
                    <StyledEditWrapper>
                        <GoBackStyled>
                            <EuiButton onClick={ goSomewhere }
                                       size="s"
                                       iconType={ 'returnKey' }
                                       color={ 'text' }>
                                Go back to Server
                            </EuiButton>
                        </GoBackStyled>
                        <EditSurfaceStyled>
                            <EditHeroStyled>
                                <div>
                                    <div className="eyebrow">MongoDB monitor settings</div>
                                    <h1>{ server ? server.name : 'Mongo management' }</h1>
                                    <div className="summary">
                                        Manage the Mongo monitor display name, labels, and lifecycle from one focused place.
                                    </div>
                                </div>
                                <div className="hero-actions">
                                    <EuiButton
                                        onClick={ goSomewhere }
                                        size="s"
                                        iconType={ 'logoMongodb' }
                                        color={ 'primary' }
                                        fill
                                    >
                                        View Mongo metrics
                                    </EuiButton>
                                </div>
                            </EditHeroStyled>

                            { server && (
                                <MongoMetaGridStyled>
                                    <MongoMetaItemStyled>
                                        <span className="label">Monitor ID</span>
                                        <span className="value" title={ server._id }>{ server._id }</span>
                                    </MongoMetaItemStyled>
                                    <MongoMetaItemStyled>
                                        <span className="label">Last update</span>
                                        <span className="value">{ server.timeAgo || 'No data yet' }</span>
                                    </MongoMetaItemStyled>
                                    <MongoMetaItemStyled>
                                        <span className="label">Hosts</span>
                                        <span className="value">{ server.numberOfHosts || 'N/A' }</span>
                                    </MongoMetaItemStyled>
                                    <MongoMetaItemStyled>
                                        <span className="label">Agent data</span>
                                        <span className="value">{ server.agentPushedTheData ? 'Receiving' : 'Waiting' }</span>
                                    </MongoMetaItemStyled>
                                </MongoMetaGridStyled>
                            ) }

                            <ManagementGridStyled>
                                <ColumnStyled>
                                    <EditZoneStyled className="bonya" accentColor="rgb(0, 128, 213)">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="database" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Edit Mongo monitor</h3>
                                                <p>Use a clear display name so this database monitor is easy to recognize in lists and alerts.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        { server && (
                                            <EditServerName server={ server }
                                                            onSave={ handleNameSave }
                                                            successMessage="Mongo monitor name changed"
                                            />
                                        ) }
                                    </EditZoneStyled>

                                    <EditZoneStyled className="bonya" accentColor="#50bc5e">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="tag" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Tags</h3>
                                                <p>Group this Mongo monitor by cluster, environment, customer, or ownership.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        <TagsWrapperStyled>
                                            { server && (
                                                <TagsList
                                                    serverId={ serverId }
                                                    serverTags={ server.tags }
                                                    monitorType={ MONITOR_TYPES.MONGO }
                                                />
                                            ) }
                                        </TagsWrapperStyled>
                                        <TagsActionsStyled>
                                            <EuiButton
                                                size="s"
                                                onClick={ () => setIsTagsModalOpen(true) }
                                                iconType="plusInCircle"
                                                fill
                                            >
                                                Edit tags
                                            </EuiButton>
                                        </TagsActionsStyled>
                                    </EditZoneStyled>
                                </ColumnStyled>

                                <ColumnStyled>
                                    <EditZoneStyled className="bonya" accentColor="#f9be49">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="inspect" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Monitor snapshot</h3>
                                                <p>Recent MongoDB telemetry pulled from the agent for quick orientation.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        <InsightListStyled>
                                            <div className="insight-row">
                                                <span className="label">Mongo host</span>
                                                <span className="value">{ serverStatus && serverStatus.host ? serverStatus.host : 'No data yet' }</span>
                                            </div>
                                            <div className="insight-row">
                                                <span className="label">Version</span>
                                                <span className="value">{ serverStatus && serverStatus.version ? serverStatus.version : 'No data yet' }</span>
                                            </div>
                                            <div className="insight-row">
                                                <span className="label">Storage used</span>
                                                <span className="value">
                                                    { server && server.totalDbUsageFormated ? `${ server.totalDbUsageFormated.value } ${ server.totalDbUsageFormated.unit }` : 'No data yet' }
                                                </span>
                                            </div>
                                            <div className="insight-row">
                                                <span className="label">Connections</span>
                                                <span className="value">
                                                    { server && server.connections !== undefined ? `${ server.connections } active` : 'No data yet' }
                                                </span>
                                            </div>
                                        </InsightListStyled>
                                    </EditZoneStyled>

                                    <DeleteServerZoneStyled className="bonya">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="trash" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Danger zone</h3>
                                                <p>Deleting this Mongo monitor removes it from monitoring and sends you back to the monitor list.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        { !areYouSureState && (
                                            <EuiButton
                                                size="s"
                                                iconType={ 'trash' }
                                                color="danger"

                                                onClick={ () => setAreYouSureState(
                                                    true) }
                                                fill
                                            >Delete Mongo monitor
                                            </EuiButton>
                                        ) }

                                        { areYouSureState && (
                                            <>
                                                <div
                                                    className="are-you-sure">Delete { server ? server.name : 'this Mongo monitor' }?
                                                </div>
                                                <DangerActionsStyled>
                                                    <EuiButton
                                                        size="s"
                                                        color="danger"

                                                        onClick={ handleDelete }
                                                        isLoading={ deleteSending }
                                                        fill
                                                    >
                                                        Yes, delete
                                                    </EuiButton>

                                                    <EuiButton
                                                        size="s"
                                                        onClick={ () => setAreYouSureState(
                                                            false) }
                                                    >
                                                        Cancel
                                                    </EuiButton>
                                                </DangerActionsStyled>
                                            </>
                                        ) }
                                    </DeleteServerZoneStyled>
                                </ColumnStyled>
                            </ManagementGridStyled>
                        </EditSurfaceStyled>
                    </StyledEditWrapper>
                </>
            ) }
            { isTagsModalOpen && (
                <TagsModal close={ () => setIsTagsModalOpen(false) }/>
            ) }
        </main>
    );

};

export default MongoEditPage;
